import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import './RoleAdmin.css';

export default function RoleAdmin() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Add/grant form
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('admin');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');

  // Permissions matrix state
  const [features, setFeatures] = useState([]);
  const [perms, setPerms] = useState({});       // { [featureKey]: { view, edit } } for 'user'
  const [permSavingKey, setPermSavingKey] = useState(null);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/roles`);
      if (resp.data.success) setRoles(resp.data.roles || []);
      else setError(resp.data.error || 'โหลดข้อมูลไม่สำเร็จ');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/roles/permissions`);
      if (resp.data.success) {
        setFeatures(resp.data.features || []);
        setPerms(resp.data.permissions?.user || {});
      }
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => { fetchRoles(); fetchPermissions(); }, [fetchRoles, fetchPermissions]);

  // Toggle one cell (user role) and persist. Editing 'edit' to true implies view.
  const togglePerm = async (featureKey, mode) => {
    const current = perms[featureKey] || { view: false, edit: false };
    const next = { ...current, [mode]: !current[mode] };
    if (mode === 'edit' && next.edit) next.view = true;   // edit needs view
    if (mode === 'view' && !next.view) next.edit = false; // hiding removes edit
    setPerms((p) => ({ ...p, [featureKey]: next }));      // optimistic
    setPermSavingKey(featureKey);
    setError('');
    try {
      const resp = await axios.post(`${API_BASE_URL}/api/roles/permissions`, {
        role: 'user', feature: featureKey, can_view: next.view, can_edit: next.edit,
      });
      if (!resp.data.success) { setError(resp.data.error || 'บันทึกไม่สำเร็จ'); fetchPermissions(); }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      fetchPermissions(); // revert to server truth
    } finally {
      setPermSavingKey(null);
    }
  };

  const handleSave = async () => {
    const e = email.trim().toLowerCase();
    if (!e) { setError('กรุณาระบุอีเมล'); return; }
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const resp = await axios.post(`${API_BASE_URL}/api/roles`, { email: e, role });
      if (resp.data.success) {
        setNotice(`บันทึกสิทธิ์ ${role} ให้ ${e} แล้ว`);
        setEmail('');
        fetchRoles();
      } else {
        setError(resp.data.error || 'บันทึกไม่สำเร็จ');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (targetEmail) => {
    if (!window.confirm(`ลบสิทธิ์ของ ${targetEmail}? ผู้ใช้จะกลับเป็น user ปกติ`)) return;
    setError('');
    setNotice('');
    try {
      const resp = await axios.delete(`${API_BASE_URL}/api/roles/${encodeURIComponent(targetEmail)}`);
      if (resp.data.success) { setNotice(`ลบสิทธิ์ ${targetEmail} แล้ว`); fetchRoles(); }
      else setError(resp.data.error || 'ลบไม่สำเร็จ');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  return (
    <div className="role-admin">
      <h2>Role Management</h2>
      <p className="role-subtitle">
        กำหนดสิทธิ์ผู้ใช้ตามอีเมล Google ที่ใช้เข้าสู่ระบบ — <b>admin</b> เห็นทุกแท็บและจัดการสิทธิ์ได้,
        {' '}<b>user</b> ใช้งานแท็บปฏิบัติการตามปกติ. ผู้ที่อยู่ใน <code>ADMIN_EMAILS</code> เป็น admin เสมอ
      </p>

      {/* Grant form */}
      <div className="role-form">
        <div className="role-form-field">
          <label>อีเมล</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@clickbroker.co.th"
            autoComplete="off"
          />
        </div>
        <div className="role-form-field">
          <label>สิทธิ์</label>
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="admin">admin</option>
            <option value="user">user</option>
          </select>
        </div>
        <button className="role-btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'กำลังบันทึก...' : 'บันทึกสิทธิ์'}
        </button>
      </div>

      {error && <div className="role-error"><span>⚠️</span>{error}</div>}
      {notice && <div className="role-notice">✅ {notice}</div>}

      {/* Roles table */}
      <div className="role-table-wrapper">
        <table className="role-table">
          <thead>
            <tr>
              <th>อีเมล</th>
              <th>สิทธิ์</th>
              <th>แหล่งที่มา</th>
              <th>ให้สิทธิ์โดย</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="role-empty">กำลังโหลด...</td></tr>
            ) : roles.length === 0 ? (
              <tr><td colSpan={5} className="role-empty">ยังไม่มีการกำหนดสิทธิ์</td></tr>
            ) : roles.map((r) => (
              <tr key={r.email}>
                <td className="font-mono">{r.email}</td>
                <td>
                  <span className={`role-badge ${r.role === 'admin' ? 'role-badge-admin' : 'role-badge-user'}`}>
                    {r.role}
                  </span>
                </td>
                <td>
                  {r.source === 'env'
                    ? <span className="role-source-env" title="กำหนดจาก ADMIN_EMAILS (env)">ENV (base admin)</span>
                    : <span className="role-source-db">DB</span>}
                </td>
                <td><small>{r.granted_by || '-'}</small></td>
                <td>
                  {r.source === 'env'
                    ? <span className="role-locked" title="ผู้ดูแลพื้นฐาน เปลี่ยนได้ที่ env เท่านั้น">🔒 ล็อก</span>
                    : <button className="role-btn-remove" onClick={() => handleRemove(r.email)}>ลบ</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Permissions matrix */}
      <h2 style={{ marginTop: 32 }}>Permissions — what a <b>user</b> can do</h2>
      <p className="role-subtitle">
        เลือกว่าผู้ใช้ทั่วไป (role = user) เห็น (View) และแก้ไข/ใช้งาน (Edit) แต่ละเมนูได้หรือไม่.
        {' '}Admin เห็นและทำได้ทุกอย่างเสมอ. การติ๊ก Edit จะเปิด View ให้อัตโนมัติ.
      </p>

      <div className="role-table-wrapper">
        <table className="role-table role-perm-table">
          <thead>
            <tr>
              <th>เมนู / ฟีเจอร์</th>
              <th>กลุ่ม</th>
              <th className="text-center">User View</th>
              <th className="text-center">User Edit</th>
            </tr>
          </thead>
          <tbody>
            {features.length === 0 ? (
              <tr><td colSpan={4} className="role-empty">กำลังโหลด...</td></tr>
            ) : features.map((f) => {
              const p = perms[f.key] || { view: false, edit: false };
              const busy = permSavingKey === f.key;
              return (
                <tr key={f.key}>
                  <td>{f.label}{f.edit && f.editLabel ? <small style={{ color: '#888' }}> — {f.editLabel}</small> : null}</td>
                  <td><small>{f.group}</small></td>
                  <td className="text-center">
                    <input type="checkbox" checked={!!p.view} disabled={busy}
                           onChange={() => togglePerm(f.key, 'view')} />
                  </td>
                  <td className="text-center">
                    {f.edit
                      ? <input type="checkbox" checked={!!p.edit} disabled={busy}
                               onChange={() => togglePerm(f.key, 'edit')} />
                      : <span style={{ color: '#ccc' }}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
